import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import V5GadgetForm from './V5GadgetForm';
import V5GadgetFormContext from '../../contexts/V5GadgetFormContext';
import ComparisonContext from '../../contexts/ComparisonContext';
import useSelectedGadget from '../../hooks/useSelectedGadget';
import useDataTypesFromSeriesAndMetricListItems from '../../hooks/useDataTypesFromSeriesAndMetricListItems';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import _ from 'lodash';

import aguid from 'lib/aguid';
import isGauge from '../../types/visTypeCheckers/isGauge';
import withDateFilter from '../../hocs/withDateFIlter';
import isRemindersGadget from '../../types/visTypeCheckers/isRemindersGadget';
import DateInputContext from '../../contexts/DateInputContext';
import isRankingMatrix from '../../types/visTypeCheckers/isRankingMatrix';
import LocalTimelineProvider from '../../contextProviders/TimelineProvider/LocalTimelineProvider';
import isEntityInformationTable from '../../types/visTypeCheckers/isEntityInformationTable';
import isPaceMatrix from '../../types/visTypeCheckers/isPaceMatrix';
import isGroupVsRemainder from '../../types/visTypeCheckers/isGroupVsRemainder';
import { FiscalPeriodsContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';
import { SortToggleContext } from './SortToggleContext';
import isSecondaryDimensionAEligible from './isSecondaryDimensionAEligible';

const dimensionCharts = [
  'bar',
  'horizontalBar',
  'pie',
  'timeSeries',
  'treeMap',
  'combo',
  'matrix',
  'stackedBar',
  'stackedArea',
  'donut',
  'radar',
  'waterfall',
  'packedBubble',
  'scatter',
  'bubble',
  'heatmap',
  'funnel',
  'pyramid',
  'sankey',
] as GadgetType[];
const multiDimensionCharts = [
  'timeSeries',
  'matrix',
  'stackedBar',
  'stackedArea',
  'heatmap',
  'sankey',
];
const multipleSeriesCharts = [
  'bar',
  'horizontalBar',
  'timeSeries',
  'combo',
  'matrix',
  'radar',
  'scatter',
  'bubble',
] as GadgetType[];

const multipleYAxisCharts = ['combo'];

const defaultStackedBarOptions = {
  direction: 'horizontal' as StackedBarDirection,
  asPercentage: false,
  altPivot: false,
};

const defaultStackedAreaOptions = {
  altPivot: false,
};

const V5GadgetFormContainer = ({
  selected,
  gadgetType,
  onChange,
}: {
  selected?: V5ChartDefinition;
  gadgetType: GadgetType;
  onChange?: (newDef: V5ChartDefinition) => void;
}) => {
  const { setDataTypes } = useContext(DateInputContext);
  const { currentFiscalYear } = useContext(FiscalPeriodsContext);
  const [isLoading] = useState<boolean>(false);
  const [id] = useState<string>(() => (selected ? selected.id : aguid()));
  const [isEditing, setIsEditing] = useState<boolean>(!!selected);
  const [name, setName] = useState<string>(selected ? selected.name : '');
  const [description, setDescription] = useState<string>(
    selected && selected.description ? selected.description : '',
  );
  const [series, setSeries] = useState<V5ChartDefinitionSeries[]>(
    selected ? selected.series : [],
  );
  const [dimensionA, setDimensionA] = useState<Dimension | undefined>(
    selected ? selected.dimensionA : undefined,
  );
  const [dimensionB, setDimensionB] = useState<Dimension | undefined>(
    selected ? selected.dimensionB : undefined,
  );
  const [secondaryDimensionA, setSecondaryDimensionA] = useState<
    Dimension | undefined
  >(selected ? selected.secondaryDimensionA : undefined);
  const [groupByLimitMode, setGroupByLimitMode] = useState<
    'top n' | 'show all' | ExcludeTopN | undefined
  >(selected ? selected.groupByLimitMode : undefined);
  const { defaultSort, setDefaultSort } = useContext(SortToggleContext);

  const [groupByLimit, setGroupByLimit] = useState<number | undefined>(
    selected ? selected.groupByLimit : undefined,
  );
  const [trendingMode, setTrendingMode] = useState<
    'calendarInterval' | 'fixedInterval' | 'hourInterval' | 'fiscalInterval'
  >(() => {
    if (!selected) {
      return 'calendarInterval';
    }
    if (selected.trendByFixedIntervalDays) {
      return 'fixedInterval';
    }
    if (selected.trendByHourInterval) {
      return 'hourInterval';
    }
    if (selected.trendByFiscalInterval) {
      return 'fiscalInterval';
    }

    return 'calendarInterval';
  });

  const [trendByCalendarInterval, setTrendByCalendarInterval] = useState<
    FleetOps.Interval | 'auto' | undefined
  >(selected ? selected.trendByCalendarInterval : undefined);
  const [trendByFiscalInterval, setTrendByFiscalInterval] = useState<
    FixedPeriods.FiscalType | undefined
  >(selected ? selected.trendByFiscalInterval : undefined);

  const [trendingModeOptions, setTrendingModeOptions] = useState<RadioOption[]>(
    [],
  );
  useEffect(() => {
    setTrendingModeOptions([
      {
        key: 'c',
        label: 'Calendar Interval',
        isSelected: trendingMode === 'calendarInterval',
        onSelected: () => setTrendingMode('calendarInterval'),
      },
      {
        key: 'f',
        label: 'Fixed Interval',
        isSelected: trendingMode === 'fixedInterval',
        onSelected: () => setTrendingMode('fixedInterval'),
      },
      {
        key: 'fi',
        label: 'Fiscal Interval',
        isSelected: trendingMode === 'fiscalInterval',
        isDisabled: !currentFiscalYear,
        onSelected: () => {
          setTrendingMode('fiscalInterval');
          setTrendByCalendarInterval(undefined);
        },
      },
      {
        key: 'h',
        label: 'Hour Interval',
        isSelected: trendingMode === 'hourInterval',
        onSelected: () => {
          setTrendingMode('hourInterval');
          setTrendByCalendarInterval(undefined);
        },
      },
    ]);
  }, [currentFiscalYear, trendingMode]);
  const [excludePartialIntervals, setExcludePartialIntervals] = useState(
    selected ? selected.excludePartialIntervals : false,
  );
  const [trendByFixedIntervalDays, setTrendByFixedIntervalDays] = useState<
    number | undefined
  >(selected ? selected.trendByFixedIntervalDays : undefined);
  const [stackedBarOptions, setStackedBarOptions] = useState<
    StackedBarOptions | undefined
  >(
    selected && selected.stackedBarOptions
      ? selected.stackedBarOptions
      : gadgetType === 'stackedBar'
        ? defaultStackedBarOptions
        : undefined,
  );
  const [stackedAreaOptions, setStackedAreaOptions] = useState<
    StackedAreaOptions | undefined
  >(
    selected && selected.stackedAreaOptions
      ? selected.stackedAreaOptions
      : gadgetType === 'stackedArea'
        ? defaultStackedAreaOptions
        : undefined,
  );
  const [gridConfig, setGridConfig] = useState<SimpleGridConfig | undefined>(
    selected && selected.simpleGridConfig
      ? selected.simpleGridConfig.fieldAliases
        ? selected.simpleGridConfig
        : {
            ...selected.simpleGridConfig,
            fieldAliases: selected.simpleGridConfig.fields.map((f) => ({
              field: f,
              alias: _.startCase(f),
            })),
          }
      : gadgetType === 'simpleGrid'
        ? {
            dataType: '',
            fields: [],
            fieldAliases: [],
            sort: [],
          }
        : undefined,
  );
  const [showDataLabels, setShowDataLabels] = useState<boolean | undefined>(
    selected ? selected.showDataLabels : false,
  );
  const [labelStep, setLabelStep] = useState<number | undefined>(
    selected ? selected.labelStep : undefined,
  );
  const [gridSizing, setGridSizing] = useState<
    'fitGadget' | 'fitContent' | undefined
  >(selected ? selected.gridSizing : 'fitGadget');
  const [reverseAxes, setReverseAxes] = useState<boolean>(
    selected ? !!selected.reverseAxes : false,
  );
  const [isSecondaryRowsStacked, setIsSecondaryRowsStacked] = useState<boolean>(
    selected ? !!selected.isSecondaryRowsStacked : false,
  );
  const [totalsColumnPosition, setTotalsColumnPosition] = useState<
    'first' | 'last'
  >(selected?.totalsColumnPosition ?? 'last');
  const [draftChart, setDraftChart] = useState<V5ChartDefinition | undefined>();
  const dataTypesFromSeries = useDataTypesFromSeriesAndMetricListItems(series);

  const grouping = useMemo(() => {
    return defaultSort ? { defaultSort } : undefined;
  }, [defaultSort]);

  const lastDefaultSortRef = useRef<SortConfig | undefined>();
  const selectedIdRef = useRef<string | undefined>();

  useEffect(() => {
    if (!selected) return;

    // Only sync from 'selected' when we're loading a different gadget
    const isNewGadget = selectedIdRef.current !== selected.id;
    if (!isNewGadget) return;

    selectedIdRef.current = selected.id;

    let newDefaultSort: SortConfig | undefined;

    if (selected.grouping?.defaultSort) {
      // Use the grouping config from the saved gadget
      newDefaultSort = {
        ...selected.grouping.defaultSort,
        isAbsolute: selected.grouping.defaultSort.isAbsolute ?? false,
      };
    } else {
      // Check for legacy sorting options
      const sortedMetric = selected.series.find((s) => s.sortByMetricId);
      if (sortedMetric) {
        let sortDirection: SortBy | undefined;
        if (sortedMetric.sortByComparison) {
          sortDirection = sortedMetric.sortByComparison.includes('asc')
            ? 'asc'
            : 'desc';
        } else {
          sortDirection = selected.groupBySortBy || 'desc';
        }

        newDefaultSort = {
          field: sortedMetric.metricId,
          isComparison: !!sortedMetric.sortByComparison,
          isAbsolute: sortedMetric.sortByComparison?.includes('abs') ?? false,
          metricId: sortedMetric.metricId,
          direction: sortDirection,
        };
      } else if (dimensionA) {
        // Fallback: sort by dimensionA field
        newDefaultSort = {
          field: dimensionA.field,
          direction: 'desc',
        };
      } else {
        // No sorting configured
        newDefaultSort = undefined;
      }
    }

    // Only update if the value has actually changed
    if (!_.isEqual(lastDefaultSortRef.current, newDefaultSort)) {
      lastDefaultSortRef.current = newDefaultSort;
      setDefaultSort(newDefaultSort);
    }
  }, [dimensionA, selected, setDefaultSort]);

  // Seed a default sort for a brand-new matrix gadget so the sort field is
  // populated on creation (the effect above only seeds sort for gadgets loaded
  // from a saved definition). Defaults to the date dimension when dimensionA is
  // a date, otherwise the first configured metric, always descending. Only sets
  // when no sort has been configured yet, so it never overrides a user choice.
  useEffect(() => {
    if (selected) return;
    if (gadgetType !== 'matrix') return;
    if (defaultSort) return;

    const isDateDimensionA = dimensionA?.fieldType === 'date';
    const field = isDateDimensionA ? dimensionA?.field : series[0]?.metricId;

    if (!field) return;

    setDefaultSort({
      field,
      metricId: isDateDimensionA ? undefined : series[0]?.metricId,
      direction: 'desc',
      isAbsolute: false,
    });
  }, [selected, gadgetType, dimensionA, series, defaultSort, setDefaultSort]);

  useEffect(() => {
    setDataTypes(dataTypesFromSeries);
  }, [dataTypesFromSeries, setDataTypes]);

  const isDimensionalChart = dimensionCharts.includes(gadgetType);
  const isMultipleDimensionChart =
    multiDimensionCharts.includes(gadgetType) && series.length < 2;
  const isMultipleSeriesChart =
    multipleSeriesCharts.includes(gadgetType) && dimensionB === undefined;
  const isMultipleYAxisChart = multipleYAxisCharts.includes(gadgetType);
  const isSecondaryDimensionAAvailable = isSecondaryDimensionAEligible({
    gadgetType,
    dimensionA,
    dimensionB,
    seriesCount: series.length,
    reverseAxes,
  });

  useEffect(() => {
    if (!selected && gadgetType === 'simpleGrid') {
      setGridConfig({
        dataType: '',
        fields: [],
        fieldAliases: [],
        sort: [],
      });
    }
  }, [gadgetType, selected]);

  const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const onDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  const onGadgetTypeChange = (newType: GadgetType) => {
    if (
      newType !== 'bar' &&
      newType !== 'horizontalBar' &&
      newType !== 'combo' &&
      newType !== 'treeMap'
    ) {
      setShowDataLabels(undefined);
    }
    if (newType === 'stackedBar') {
      setStackedBarOptions(defaultStackedBarOptions);
    } else {
      setStackedBarOptions(undefined);
    }

    if (newType === 'stackedArea') {
      setStackedAreaOptions(defaultStackedAreaOptions);
    } else {
      setStackedAreaOptions(undefined);
    }

    if (newType === 'simpleGrid') {
      setGridConfig({
        dataType: '',
        fields: [],
        sort: [],
        fieldAliases: [],
      });
      setSeries([]);
    } else {
      setGridConfig(undefined);
    }
  };

  const onGroupByLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
    setGroupByLimit(Number(event.target.value));
  };

  const onTrendByCalendarIntervalChange = (
    newValue: FleetOps.Interval | 'auto',
  ) => {
    setTrendByCalendarInterval(newValue);
  };

  const onTrendByFiscalIntervalChange = (newValue: FixedPeriods.FiscalType) => {
    setTrendByFiscalInterval(newValue);
  };

  const onExcludePartialIntervalsChange = (newValue: boolean) => {
    setExcludePartialIntervals(newValue);
  };

  const onTrendByFixedIntervalChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setTrendByFixedIntervalDays(Number(event.target.value));
  };

  const isShowingGroupingInputs = (() => {
    if (dimensionA) {
      if (
        dimensionA.fieldType === 'text' ||
        dimensionA.fieldType === 'boolean' ||
        dimensionA.fieldType === 'date'
      ) {
        return true;
      }
    }

    if (dimensionB) {
      if (
        dimensionB.fieldType === 'text' ||
        dimensionB.fieldType === 'boolean'
      ) {
        return true;
      }
    }

    return false;
  })();

  const isShowingSortInputs = (() => {
    // Matrix always supports sorting (incl. date-only matrices)
    if (gadgetType === 'matrix') {
      return true;
    }
    // Other charts: only when grouped by a categorical dimension.
    // Date dimensions are excluded — ordering is chronological (Trending),
    // so sorting by a date field doesn't make sense.
    if (
      dimensionA &&
      (dimensionA.fieldType === 'text' || dimensionA.fieldType === 'boolean')
    ) {
      return true;
    }
    if (
      dimensionB &&
      (dimensionB.fieldType === 'text' || dimensionB.fieldType === 'boolean')
    ) {
      return true;
    }
    return false;
  })();

  const isShowingTrendingInputs = (() => {
    if (dimensionA) {
      if (dimensionA.fieldType === 'date' && dimensionA.field !== 'dayOfWeek') {
        return true;
      }
    }

    if (dimensionB) {
      if (dimensionB.fieldType === 'date' && dimensionB.field !== 'dayOfWeek') {
        return true;
      }
    }

    return false;
  })();

  const isGroupingByDayOfWeek = (() => {
    /**
     * Some datasets have a dayOfWeek fields while others do not.
     * The UI will inject a "dayOfWeek" field which SE can slice data by
     * in the cases where it is not available
     *
     * In these cases, isGroupByDayOfWeek will be true
     * If the dataset has dayOfWeek, isGroupByDayOfWeek will be false
     */
    if (dimensionA) {
      if (dimensionA.field === 'dayOfWeek' && dimensionA.isGroupByDayOfWeek) {
        return true;
      }
    }

    if (dimensionB) {
      if (dimensionB.field === 'dayOfWeek' && dimensionB.isGroupByDayOfWeek) {
        return true;
      }
    }
  })();

  const isGroupingByMonth = (() => {
    /**
     * Unclear to me if we need this like how we do for day of week.
     * Adding it to be on the safe side.
     */
    if (dimensionA) {
      if (dimensionA.field === 'month' && dimensionA.isGroupByMonth) {
        return true;
      }
    }

    if (dimensionB) {
      if (dimensionB.field === 'month' && dimensionB.isGroupByMonth) {
        return true;
      }
    }
  })();

  useEffect(() => {
    if (trendingMode === 'calendarInterval') {
      setTrendByFixedIntervalDays(undefined);
      setTrendByFiscalInterval(undefined);
    }
    if (trendingMode === 'fixedInterval') {
      setTrendByCalendarInterval(undefined);
      setTrendByFiscalInterval(undefined);
    }
    if (trendingMode === 'fiscalInterval') {
      setTrendByCalendarInterval(undefined);
      setTrendByFixedIntervalDays(undefined);
    }
  }, [trendingMode]);

  useEffect(() => {
    if (gadgetType === 'timeSeries') {
      setDimensionA({
        field: 'date',
        fieldType: 'date',
        isGroupByDayOfWeek: false,
        isGroupByMonth: false,
      });
    }
  }, [gadgetType]);

  useEffect(() => {
    if (!isShowingTrendingInputs) {
      setTrendByFixedIntervalDays(undefined);
      setTrendByCalendarInterval(undefined);
      setTrendByFiscalInterval(undefined);
    } else if (trendingMode === 'calendarInterval') {
      if (!trendByCalendarInterval) {
        setTrendByCalendarInterval('auto');
      }
    } else if (trendingMode === 'fiscalInterval') {
      setTrendByFiscalInterval('fiscalPeriod');
    }
  }, [isShowingTrendingInputs, trendByCalendarInterval, trendingMode]);

  useEffect(() => {
    if (!isMultipleSeriesChart && series.length > 1) {
      setSeries([series[0]]);
    }

    if (!isDimensionalChart) {
      setDimensionA(undefined);
      setDimensionB(undefined);
    }
  }, [series, isMultipleSeriesChart, isDimensionalChart]);

  useEffect(() => {
    if (
      !isSecondaryDimensionAAvailable ||
      (secondaryDimensionA &&
        dimensionA &&
        secondaryDimensionA.field === dimensionA.field)
    ) {
      setSecondaryDimensionA(undefined);
    }
  }, [dimensionA, isSecondaryDimensionAAvailable, secondaryDimensionA]);

  // Nothing to stack without a secondary dimension: picking one again starts
  // flat
  useEffect(() => {
    if (!secondaryDimensionA) {
      setIsSecondaryRowsStacked(false);
    }
  }, [secondaryDimensionA]);

  useEffect(() => {
    setIsEditing(!!selected);
  }, [selected]);

  useEffect(() => {
    if (
      !isMultipleYAxisChart &&
      (series.some((s) => s.opposite) ||
        series.some((s) => s.type) ||
        series.some((s) => s.plotOnSeparateAxis))
    ) {
      setSeries(
        series.map((s) => ({
          id: s.id,
          metricId: s.metricId,
          smartCardComparison: 'delta',
        })),
      );
    }
  }, [series, isMultipleYAxisChart]);

  useEffect(() => {
    if (series.length === 0 && gridConfig === undefined) {
      setDraftChart(undefined);
      return;
    }
    const newDraftChart: V5ChartDefinition = {
      id,
      description,
      name,
      gadgetType,
      series,
      groupByLimit,
      groupByLimitMode,
      trendByCalendarInterval,
      trendByFiscalInterval,
      trendByFixedIntervalDays,
      excludePartialIntervals,
      groupByDayOfWeek: isGroupingByDayOfWeek,
      groupByMonth: isGroupingByMonth,
      trendByHourInterval: trendingMode === 'hourInterval',
      dimensionA,
      secondaryDimensionA,
      // Only with a secondary dimension to stack
      isSecondaryRowsStacked:
        secondaryDimensionA && isSecondaryRowsStacked ? true : undefined,
      dimensionB,
      stackedBarOptions,
      stackedAreaOptions,
      simpleGridConfig: gridConfig,
      showDataLabels,
      labelStep,
      gridSizing,
      reverseAxes,
      totalsColumnPosition,
      grouping,
    };

    setDraftChart(newDraftChart);
  }, [
    description,
    dimensionA,
    dimensionB,
    excludePartialIntervals,
    gadgetType,
    gridConfig,
    groupByLimit,
    groupByLimitMode,
    grouping,
    id,
    isGroupingByDayOfWeek,
    isGroupingByMonth,
    isSecondaryRowsStacked,
    gridSizing,
    labelStep,
    reverseAxes,
    secondaryDimensionA,
    totalsColumnPosition,
    name,
    series,
    showDataLabels,
    stackedAreaOptions,
    stackedBarOptions,
    trendByCalendarInterval,
    trendByFiscalInterval,
    trendByFixedIntervalDays,
    trendingMode,
  ]);

  useEffect(() => {
    if (!!draftChart && !!onChange) {
      onChange(draftChart);
    }
  }, [draftChart, onChange]);

  const isValidChart = (() => {
    if (
      gadgetType === 'stackedArea' ||
      gadgetType === 'stackedBar' ||
      gadgetType === 'heatmap' ||
      gadgetType === 'sankey'
    ) {
      return !!dimensionA && !!dimensionB;
    }
    return true;
  })();

  const gridSizingOptions = useMemo((): DropdownOption[] => {
    return [
      {
        label: 'Fit Gadget',
        subLabel: 'Attempt to fit all columns within the gadget space',
        isSelected: gridSizing === 'fitGadget' || gridSizing === undefined,
        onSelected: () => {
          setGridSizing('fitGadget');
        },
      },
      {
        label: 'Fit Content',
        subLabel: 'Size columns to fit their content',
        isSelected: gridSizing === 'fitContent',
        onSelected: () => {
          setGridSizing('fitContent');
        },
      },
    ];
  }, [gridSizing]);

  return (
    <ComparisonContext.Provider
      value={{
        currentComparison: {
          compareType: 'previous',
        },
        setCurrentComparison: () => {},
      }}
    >
      <V5GadgetFormContext.Provider
        value={{
          isEditing,
          series,
          setSeries,
          dimensionA,
          setDimensionA,
          secondaryDimensionA,
          setSecondaryDimensionA,
          isSecondaryDimensionAAvailable,
          dimensionB,
          setDimensionB,
          stackedBarOptions,
          setStackedBarOptions,
          stackedAreaOptions,
          setStackedAreaOptions,
          isLoading,
          draftChart,
          isDimensionalChart,
          isMultipleDimensionChart,
          isMultipleSeriesChart,
          isMultipleYAxisChart,
          showDataLabels,
          setShowDataLabels,
          labelStep,
          setLabelStep,
          reverseAxes,
          setReverseAxes,
          isSecondaryRowsStacked,
          setIsSecondaryRowsStacked,
          totalsColumnPosition,
          setTotalsColumnPosition,
          groupByLimitMode,
          setGroupByLimitMode,
          setGroupByLimit,
        }}
      >
        <V5GadgetForm
          name={name}
          onNameChange={onNameChange}
          description={description}
          onDescriptionChange={onDescriptionChange}
          gadgetType={gadgetType}
          onGadgetTypeChange={onGadgetTypeChange}
          isShowingSortInputs={isShowingSortInputs}
          isShowingGroupingInputs={isShowingGroupingInputs}
          groupByLimit={groupByLimit}
          onGroupByLimitChange={onGroupByLimitChange}
          isShowingTrendingInputs={isShowingTrendingInputs}
          trendingMode={trendingMode}
          trendingModeOptions={trendingModeOptions}
          trendByCalendarInterval={trendByCalendarInterval}
          onTrendByCalendarIntervalChange={onTrendByCalendarIntervalChange}
          trendByFiscalInterval={trendByFiscalInterval}
          onTrendByFiscalIntervalChange={onTrendByFiscalIntervalChange}
          excludePartialIntervals={!!excludePartialIntervals}
          onExcludePartialIntervalsChange={onExcludePartialIntervalsChange}
          trendByFixedIntervalDays={trendByFixedIntervalDays}
          onTrendByFixedIntervalChange={onTrendByFixedIntervalChange}
          draftChart={draftChart}
          isDimensionalChart={isDimensionalChart}
          isValidChart={isValidChart}
          gridConfig={gridConfig}
          setGridConfig={setGridConfig}
          isGadgetBuilder={false}
          gridSizingOptions={gridSizingOptions}
        />
      </V5GadgetFormContext.Provider>
    </ComparisonContext.Provider>
  );
};

const SelectedProvider = ({
  gadgetType,
  dashboardGadgetChart,
  onChange,
}: {
  gadgetType: GadgetType;
  dashboardGadgetChart?: VisualisationDefinition;
  onChange?: (newDef: V5ChartDefinition) => void;
}) => {
  const { selected } = useSelectedGadget(dashboardGadgetChart);

  if (isSingleMetricDateMatrix(selected)) {
    return null;
  }
  if (isGauge(selected)) {
    return null;
  }
  if (isRemindersGadget(selected)) {
    return null;
  }
  if (isRankingMatrix(selected)) {
    return null;
  }
  if (isPaceMatrix(selected)) {
    return null;
  }
  if (isEntityInformationTable(selected)) {
    return null;
  }
  if (isGroupVsRemainder(selected)) {
    return null;
  }

  return (
    <LocalTimelineProvider sources={window.emptyArray}>
      <V5GadgetFormContainer
        key={selected ? selected.id : undefined}
        selected={selected}
        gadgetType={gadgetType}
        onChange={onChange}
      />
    </LocalTimelineProvider>
  );
};

export default withDateFilter(SelectedProvider, {});
